import React, { useState, useEffect } from 'react';
import { GitPullRequest, GitCommit, Star, GitBranch, Code2, Shield, Zap } from 'lucide-react';

const GitHubStats = () => {
  const [stats, setStats] = useState({
    pullRequests: 0,
    commits: 0,
    repositories: 0,
    codeReviews: 0,
    securityFixes: 0,
    contributions: 0
  });

  useEffect(() => {
    const initialStats = {
      pullRequests: 12009,
      commits: 30050,
      repositories: 42,
      codeReviews: 2509,
      securityFixes: 89,
      contributions: 200
    };
    
    // Animate counters on mount
    const animateStats = () => {
      Object.keys(initialStats).forEach((key, index) => {
        setTimeout(() => {
          setStats(prev => ({
            ...prev,
            [key]: initialStats[key as keyof typeof initialStats]
          }));
        }, index * 200);
      });
    };

    animateStats();
  }, []);

  const statItems = [
    {
      icon: GitPullRequest,
      label: 'Pull Requests',
      value: stats.pullRequests,
      color: 'text-green-500',
      description: 'Merged PRs'
    },
    {
      icon: Code2,
      label: 'Code Reviews',
      value: stats.codeReviews,
      color: 'text-blue-500',
      description: 'Peer Reviews'
    },
    {
      icon: GitBranch,
      label: 'Repositories',
      value: stats.repositories,
      color: 'text-purple-500',
      description: 'Active Projects'
    },
    {
      icon: GitCommit,
      label: 'Total Commits',
      value: stats.commits,
      color: 'text-orange-500',
      description: 'Code Changes'
    },
    {
      icon: Shield,
      label: 'Security Fixes',
      value: stats.securityFixes,
      color: 'text-red-500',
      description: 'Vulnerability Patches'
    },
    {
      icon: Zap,
      label: 'Contributions',
      value: stats.contributions,
      color: 'text-yellow-500',
      description: 'Open Source'
    }
  ];

  return (
    <div className="bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 shadow-lg backdrop-blur-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          GitHub Activity
        </h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {statItems.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.label}
              className="group flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:scale-105 transition-transform duration-200"
            >
              <div className="p-2 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-lg group-hover:scale-110 transition-transform duration-300">
                <Icon className={`w-5 h-5 ${item.color}`} />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {item.value.toLocaleString()}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  {item.label}
                </div>
                <div className="text-[10px] text-gray-500 dark:text-gray-500">
                  {item.description}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
        Last updated: {new Date().toLocaleDateString()}
      </div>
    </div>
  );
};

export default GitHubStats;
